import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './SelectionScreen.css';

import rijaBg from '../assets/rija_selection.png';
import sreeBg from '../assets/sree_selection.jpg';
import rijaTop from '../assets/rija_top.png';
import rijaBottom from '../assets/rija_bottom.png';
import sreeTop from '../assets/sree_top.png';
import sreeBottom from '../assets/sree_bottom.png';
import frame from '../assets/frame.png';

import redLuma from '../assets/SMG_Asset_Model_Luma_(Red).png';
import blueLuma from '../assets/SMG_Asset_Model_Luma_(Blue).png';
import greenLuma from '../assets/SMG_Asset_Model_Luma_(Green).png';
import pinkLuma from '../assets/SMG_Asset_Model_Luma_(Pink).png';
import yellowLuma from '../assets/SMG_Asset_Model_Luma_(Yellow).png';

const EXTRA_LUMAS = [redLuma, blueLuma, greenLuma, pinkLuma, yellowLuma];

const LUMA_CONFIG = [
  { src: redLuma, startX: 100, startY: 150 },
  { src: blueLuma, startX: 200, startY: 300 },
  { src: greenLuma, startX: 150, startY: 400 },
  { src: pinkLuma, startX: window.innerWidth * 0.7, startY: 200 },
  { src: yellowLuma, startX: window.innerWidth * 0.8, startY: 400 },
  ...EXTRA_LUMAS.map(src => ({
    src,
    startX: Math.random() * (window.innerWidth - 60),
    startY: Math.random() * (window.innerHeight - 60),
  }))
];

function FloatingLuma({ config, allRefs, containerRef, buttonRefs, frameRefs }) {
  const ref = useRef();
  const pos = useRef({
    x: config.startX,
    y: config.startY,
    dx: (Math.random() * 2 + 1) * (Math.random() < 0.5 ? 1 : -1),
    dy: (Math.random() * 2 + 1) * (Math.random() < 0.5 ? 1 : -1),
    width: 60,
    height: 60
  });

  React.useEffect(() => {
    let animationFrame;

    const animate = () => {
      const elem = ref.current;
      if (!elem) return;

      const width = pos.current.width;
      const height = pos.current.height;

      // Move
      pos.current.x += pos.current.dx;
      pos.current.y += pos.current.dy;

      // Border collision
      if (pos.current.x <= 0 || pos.current.x + width >= window.innerWidth) pos.current.dx *= -1;
      if (pos.current.y <= 0 || pos.current.y + height >= window.innerHeight) pos.current.dy *= -1;

      // Button collision
      buttonRefs.forEach(btnRef => {
        const btn = btnRef.current;
        if (!btn) return;
        const rect = btn.getBoundingClientRect();
        if (
          pos.current.x < rect.right &&
          pos.current.x + width > rect.left &&
          pos.current.y < rect.bottom &&
          pos.current.y + height > rect.top
        ) {
          pos.current.dx *= -1;
          pos.current.dy *= -1;
        }
      });

      // Frame collision
      frameRefs.forEach(frameRef => {
        const frameEl = frameRef.current;
        if (!frameEl) return;
        const rect = frameEl.getBoundingClientRect();
        if (
          pos.current.x < rect.right &&
          pos.current.x + width > rect.left &&
          pos.current.y < rect.bottom &&
          pos.current.y + height > rect.top
        ) {
          pos.current.dx *= -1;
          pos.current.dy *= -1;
        }
      });

      // Luma collision
      allRefs.forEach(otherRef => {
        const other = otherRef.current;
        if (!other || other === elem) return;
        const rect = other.getBoundingClientRect();
        if (
          pos.current.x < rect.right &&
          pos.current.x + width > rect.left &&
          pos.current.y < rect.bottom &&
          pos.current.y + height > rect.top
        ) {
          pos.current.dx *= -1;
          pos.current.dy *= -1;
        }
      });

      elem.style.transform = `translate(${pos.current.x}px, ${pos.current.y}px)`;
      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [allRefs, buttonRefs, frameRefs]);

  return <img ref={ref} src={config.src} className="floating-object" alt="floating luma" />;
}

export default function SelectionScreen() {
  const navigate = useNavigate();
  const containerRef = useRef();
  const rijaButtonRef = useRef();
  const sreeButtonRef = useRef();

  const rijaTopFrameRef = useRef();
  const rijaBottomFrameRef = useRef();
  const sreeTopFrameRef = useRef();
  const sreeBottomFrameRef = useRef();

  const frameRefs = [
    rijaTopFrameRef,
    rijaBottomFrameRef,
    sreeTopFrameRef,
    sreeBottomFrameRef
  ];

  const allLumaRefs = useRef([]);
  allLumaRefs.current = [];

  return (
    <div className="selection-container" ref={containerRef}>
      {/* Rija side */}
      <div className="half rija" style={{ backgroundImage: `url(${rijaBg})` }}>
        <button
          ref={rijaButtonRef}
          className="shop-button"
          onClick={() => navigate('/rija')}
        >
          Rija
        </button>

        <div className="photo-frame-container top-frame" ref={rijaTopFrameRef}>
          <img src={frame} className="photo-frame"/>
          <div className="photo-content">
            <img src={rijaTop} alt='Rija top'/>
          </div>
        </div>

        <div className="photo-frame-container bottom-frame" ref={rijaBottomFrameRef}>
          <img src={frame} className="photo-frame"/>
          <div className="photo-content">
            <img src={rijaBottom} alt="Rija bottom"/>
          </div>
        </div>
      </div>

      {/* Sree side */}
      <div className="half sree" style={{ backgroundImage: `url(${sreeBg})` }}>
        <button
          ref={sreeButtonRef}
          className="shop-button"
          onClick={() => navigate('/sree')}
        >
          Sree
        </button>

        <div className="photo-frame-container top-frame" ref={sreeTopFrameRef}>
          <img src={frame} className="photo-frame"/>
          <div className="photo-content">
            <img src={sreeTop} alt='Sree top'/>
          </div>
        </div>

        <div className="photo-frame-container bottom-frame" ref={sreeBottomFrameRef}>
          <img src={frame} className="photo-frame"/>
          <div className="photo-content">
            <img src={sreeBottom} alt="Sree bottom"/>
          </div>
        </div>
      </div>

      {LUMA_CONFIG.map((config, idx) => {
        const lumaRef = React.createRef();
        allLumaRefs.current.push(lumaRef);
        return (
          <FloatingLuma
            key={idx}
            config={config}
            containerRef={containerRef}
            buttonRefs={[rijaButtonRef, sreeButtonRef]}
            frameRefs={frameRefs}
            allRefs={allLumaRefs.current}
          />
        );
      })}
    </div>
  );
}
